<template>
  <div v-if="isDataLoaded && !isNoDataFetch" class="budget-details-container">
    <div class="budget-summary-header-container">
      <div class="budget-summary-header-title">Budget Summary</div>
      <div class="header-actions">
        <div class="custom-select">
            <select class="right-div" id="budget-type" v-model="viewByTGU" @change="setViewMode">
                <option :value="VIEW_MODES_TGU.VIEW_BY_TOTAL">View By Total</option>
                <option :value="VIEW_MODES_TGU.VIEW_BY_GSF">View per $/GSF</option>
                <option :value="VIEW_MODES_TGU.VIEW_BY_UNIT">View per $/Unit</option>
            </select>
        </div>
      </div>
    </div>
    <div class="chart-container">
      <!-- Doughnut chart-->
      <div data-test-id="doughnut-chart-content" class="doughnut-chart-container">
        <div class="chart-title">Total Project Costs</div>
        <div class="chart-subtitle">Actual Total Costs</div>
        <div class="doughnut-container">
          <div class="doughnut-chart-wrapper">
            <Doughnut ref="doughnutChartRef" class="summary-doughnut-canvas" :height=250 :width=225 :data="doughnutData"
              :options="doughnutOptions" />
            <!-- 🔥 FIX: HTML overlay for center text - Vue reactivity ensures it updates -->
            <div class="doughnut-center-text">
              <div class="doughnut-center-value">{{ kMillionsWithDecimalsFormat(totalRevisedBudget) }}</div>
              <div class="doughnut-center-label">Total Project Costs</div>
            </div>
          </div>
          <div class="doughnut-chart-legend">
            <div class="acquisition-legend">Acquisition
              <div class="detail-container">
                <div>{{ doughnutChartPercentage[0] }}</div>
                <div class="vertical-divider"></div>
                <div>{{ kMillionsWithDecimalsFormat(lcTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="hard-legend">Hard
              <div class="detail-container">
                <div>{{ doughnutChartPercentage[1] }}</div>
                <div class="vertical-divider"></div>
                <div>{{ kMillionsWithDecimalsFormat(hcTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="soft-legend">Soft
              <div class="detail-container">
                <div>{{ doughnutChartPercentage[2] }}</div>
                <div class="vertical-divider"></div>
                <div>{{ kMillionsWithDecimalsFormat(scTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="misc-finance-legend">Misc/Finance
              <div class="detail-container">
                <div>{{ doughnutChartPercentage[3] }}</div>
                <div class="vertical-divider"></div>
                <div>{{ kMillionsWithDecimalsFormat(miscFinancingTotalRevisedBudget) }}</div>
              </div>
            </div>
            <div class="contingencies-legend">Contingencies
              <div class="detail-container">
                <div>{{ doughnutChartPercentage[4] }}</div>
                <div class="vertical-divider"></div>
                <div>{{ kMillionsWithDecimalsFormat(contingenciesTotalRevisedBudget) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Bar Chart -->
      <div class="bar-chart-container">
        <!-- Hard Costs stacked bar -->
        <div class="right-chart-subcontainer">
          <div class="title-container">
            <div class="chart-title">Budget Variance Summary</div>
          </div>
          <div class="stacked-charts-container">
            <div class="hard-costs-container">
              <div class="chart summary-stacked-bar">
                <div class="chart-subtitle">Hard Costs</div>
                <Bar ref="hcBarChartRef" :height="265" :width="120" :data="hardCostsSummaryStackedBarData"
                  :options="hardCostsSummaryStackedBarOptions" />
              </div>
              <div class="chart hc-offenders-stacked-bar">
                <div class="chart-subtitle avg-summary-container">
                  <div class="avg-subtitle">Budget</div>
                  <div class="summary-value avg-value-original">{{ currencyFormat(hcSummaryAvgOriginal) }}{{ viewModeLabel }}</div>
                  <div class="avg-subtitle">Actual</div>
                  <div class=" summary-value avg-value-revised">{{ currencyFormat(hcSummaryAvgRevised) }}{{ viewModeLabel }}</div>
                </div>
                <Bar ref="hcOffendersChartRef" class="hc-offenders-stacked-bar-canvas" :height="345" :width="250" :data="hardCostsStackedBarData"
                  :options="hardCostsStackedBarOptions" />
              </div>
            </div>
            <!-- Soft Costs stacked bar -->
            <div class="soft-costs-container">
              <div class="chart-title"></div>
              <div class="chart summary-stacked-bar">
                <div class="chart-subtitle">Soft Costs</div>
                <Bar :height="265" :width="120" :data="softCostsSummaryStackedBarData"
                  :options="softCostsSummaryStackedBarOptions" />
              </div>
              <div class="chart sc-offenders-stacked-bar">
                <div class="chart-subtitle avg-summary-container">
                  <div class="avg-subtitle">Budget</div>
                  <div class="summary-value avg-value-original">{{ currencyFormat(scSummaryAvgOriginal) }}{{ viewModeLabel }}</div>
                  <div class="avg-subtitle">Actual</div>
                  <div class="summary-value avg-value-revised">{{ currencyFormat(scSummaryAvgRevised) }}{{ viewModeLabel }}</div>
                </div>
                <Bar ref="scOffendersChartRef" class="sc-offenders-stacked-bar-canvas" :height="345" :width="250" :data="softCostsStackedBarData"
                  :options="softCostsStackedBarOptions" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="budget-tracking-header-container">
      <div class="budget-tracking-header-title">Budget Tracking</div>
      <!-- 🔥 HIDDEN per user request - Subject Property Square Feet input -->
      <div class="subject-property-container" v-if="false">
        <form @submit.prevent>
          <label for="subjectPropertySqFt">Subject Property Square Feet:</label>
          <input 
            type="text" 
            id="subjectPropertySqFt" 
            v-model="subjectPropertySqFt" 
            placeholder="Enter square feet"
          />
          <button 
            type="button" 
            class="apply-button" 
            @click="updateSubjectPropertyValues"
          >
            Apply
          </button>
        </form>
      </div>
    </div>
    <div class="grid-container" id="budgetViewGrid">
      <BudgetGrid ref="budgetGridRef" :shouldCalculateGrandTotal="true" @setChartData="setChartData"></BudgetGrid>
    </div>
  </div>

</template>
<script setup>
/* eslint-disable */
import { axiosQuery, currencyFormat, currencyFormatRoundedDecimal, kMillionsFormat, kMillionsWithDecimalsFormat, percentFormat, standardizeLabelsLength } from "@/assets/js/utils";
import { useAuthStore } from "@/stores/auth_store";
import { useCompSetStore } from "@/stores/comp_set_store";
import { useMathSummaryStore } from "@/stores/math_summary_store";
import { useBudgetDataStore } from "@/stores/budget_data_store";
import { useErrorStore } from "@/stores/error_store";
import { computed, onMounted, reactive, ref, } from "vue";
import { Bar, Doughnut } from "vue-chartjs"; // note this is a publicly available Chart JS wrapper, MIT licensed
import BudgetGrid from "@/components/helpers/BudgetGrid.vue";
import { doughnutMiddleTextPlugin, standardDoughnutChartJson, standardStackedBarChartJson } from "@/assets/js/chart_config";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Colors,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { VIEW_MODES_TGU } from "@/types/Constants";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors,
  ArcElement,
  doughnutMiddleTextPlugin(),
  ChartDataLabels
);
let isDataLoaded = computed(() => {
  return isHCSCCostPerGSFDataLoaded.value
})
const budgetDataStore = useBudgetDataStore()
let isNoDataFetch = computed(() => {
  return !budgetDataStore.getIsBudgetDataLoaded
})
const compSetStore = useCompSetStore()
const mathSummaryStore = useMathSummaryStore()
let compSetCount = ref(Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).length)
let withSelectedProject = ref(true)

const labelMaxLength = 15;
let doughnutViewMode = ref('total') // this toggles between total view and per GSF
let doughnutLabel = computed(() => {
  if (doughnutViewMode.value === 'total') {
    return 'Total Project Costs'
  } else if (doughnutViewMode.value === 'gsf') {
     return 'Avg. $/GSF'
  } else if (doughnutViewMode.value === 'unit'){
    return 'Avg. $/Unit'
  } else {
    return 'Total Project Costs'
  }
})
let doughnutSummaryValue = computed(() => {
    return kMillionsWithDecimalsFormat(totalRevisedBudget.value)
})
/*
 * Doughnut Chart
*/
let totalRevisedBudget = computed(() => {
  // 🔥 FIX: Parse as floats - values might be strings from API
  return (parseFloat(lcTotalRevisedBudget.value) || 0) + 
         (parseFloat(hcTotalRevisedBudget.value) || 0) + 
         (parseFloat(scTotalRevisedBudget.value) || 0) +
         (parseFloat(miscFinancingTotalRevisedBudget.value) || 0) + 
         (parseFloat(contingenciesTotalRevisedBudget.value) || 0)
})
let avgCostsPerGSF = ref(0)
let avgRevisedBudgetPerGSF = computed(() => {
  return avgCostsPerGSF.value
});
let doughnutChartData = ref([])
const doughnutData = computed(() => {
  return {
    labels: ["Acquisition", "Hard", "Soft", "Misc/Finance", "Contingencies"],
    datasets: [
      {
        backgroundColor: [
          "#52596D", // $darkGray1
          "#4A5FD5", // $brightBlue1
          "#4A8FD0", // $mediumBlue1
          "#8A92A9", // $mediumGray1
          "#d3d3d3" // assigning to light gray for now
        ],
        data: doughnutChartData.value
      },
    ],
  };
});
let doughnutChartJsonConfig = standardDoughnutChartJson({
  doughnutTitle: "Project Costs (actuals only)"
})
const doughnutOptions = computed(() => {
  // 🔥 FIX: Return a fresh object each time to ensure Chart.js detects changes
  // The middle text plugin needs to get updated values
  return {
    ...doughnutChartJsonConfig,
    cutout: 70,
    plugins: {
      ...doughnutChartJsonConfig.plugins,
      legend: { display: false },
      doughnutMiddleTextPlugin: {
        // 🔥 FIX: Disable plugin center text - we use HTML overlay instead for reactivity
        middleTextLabel: undefined,
        middleTextValue: undefined,
        fontSize: 14
      },
      tooltip: {
        ...doughnutChartJsonConfig.plugins?.tooltip,
        callbacks: {
          label: function (context) {
            let actual = 0;
            if (context.label?.includes("Hard")) {
              actual = currencyFormat(hcTotalRevisedBudget.value)
            } else if (context.label?.includes("Soft")) {
              actual = currencyFormat(scTotalRevisedBudget.value)
            } else if (context.label?.includes("Acquisition")) {
              actual = currencyFormat(lcTotalRevisedBudget.value)
            } else if (context.label?.includes("Misc")) {
              actual = currencyFormat(miscFinancingTotalRevisedBudget.value)
            } else if (context.label?.includes("Contingencies")) {
              actual = currencyFormat(contingenciesTotalRevisedBudget.value)
            }
            return actual;
          },
          footer: function (context) {
            let variancePerc = 0;
            let varianceVal = 0;
            if (context[0].label?.includes("Hard")) {
              varianceVal = kMillionsFormat(hcTotalVariance.value);
              variancePerc = percentFormat(hcVariancePercent.value);
            } else if (context[0].label?.includes("Soft")) {
              varianceVal = kMillionsFormat(scTotalVariance.value);
              variancePerc = percentFormat(scVariancePercent.value);
            } else if (context[0].label?.includes("Acquisition")) {
              varianceVal = kMillionsFormat(lcTotalVariance.value);
              variancePerc = percentFormat(lcVariancePercent.value);
            } else if (context[0].label?.includes("Misc")) {
              varianceVal = kMillionsFormat(miscFinancingTotalVariance.value);
              variancePerc = percentFormat(miscFinancingVariancePercent.value);
            } else if (context[0].label?.includes("Contingencies")) {
              varianceVal = kMillionsFormat(contingenciesTotalVariance.value);
              variancePerc = percentFormat(contingenciesVariancePercent.value);
            }
            return `Variance: ${varianceVal} (${variancePerc})`
          },
        }
      }
    },
    responsive: true
  };
});

/***
 Stacked Bar Charts
***/

// Hard Costs Summary
const hardCostsSummaryStackedBarData = computed(() => {
  // Do not show a negative variance on the chart as the chart looks really funky
  // adjusted sov - original sov (delta value)
  let changeInBudget = hcTotalVariance.value
  if (changeInBudget < 0) {
    changeInBudget = 0
  }
  return {
    labels: ["Total"],
    datasets: [
      {
        label: "Original Budget",
        backgroundColor: "#4A5FD5", // $brightBlue1
        data: [hcTotalOriginalBudget.value], // original sov
      },
      {
        label: "Budget Variance",
        backgroundColor: "#84CADE", // $lightBlue1, css var doesn't work here
        data: [changeInBudget],
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let hardSummaryJsonConfig = standardStackedBarChartJson({
  barPercentage: 1,
  xTicksFontSize: 12,
  xTicksFont: 'Inter-Bold',
  yMaxTicksLimit: 10,
})
const hardCostsSummaryStackedBarOptions = computed(() => {
  let json = hardSummaryJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      let multilineTooltip = [label + ":"];
      multilineTooltip.push(currencyFormat(value));
      return multilineTooltip;
    },
    footer: function (context) {
      const index = context[0].dataIndex;
      if (hcTotalOriginalBudget.value === 0) {
        return "Variance: ∞ (Original budget was $0)";
      }
      return (
        "Variance: " +
        percentFormat(
          (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
          hcTotalOriginalBudget.value
        )
      )
    }
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        if (hcTotalOriginalBudget.value === 0) {
          return "∞";
        }
        return `${Math.round(parseFloat((
          (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
          hcTotalOriginalBudget.value
        )).toFixed(2) * 100)}%`
      } else {
        return ''
      }
    }
  }
  
  // Add onClick event handler for the summary chart
  json.onClick = function(event, elements) {
    if (!elements || elements.length === 0) return;
    
    // When clicking on the summary chart, we'll scroll to the Hard Costs section in the grid
    const hardCostsSection = document.querySelector('.hard-costs-container');
    if (hardCostsSection) {
      hardCostsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Show a notification
      showNotification("Hard Costs Summary", "hardCost");
    }
  };
  
  return json
})
// Hard Costs Top Offenders
const hardCostsStackedBarData = computed(() => {
  return {
    labels: standardizeLabelsLength(hcStackedBarLabels.value),
    datasets: [
      {
        label: "Original Budget",
        backgroundColor: "#4A5FD5", // $brightBlue1
        data: hcOriginalBudgetArr.value, // original sov
      },
      {
        label: "Budget Variance",
        backgroundColor: "#84CADE", // $lightBlue1
        data: hcBudgetVarianceArr.value, // adjusted sov - original sov (delta value)
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let hardStackedBarJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.8,
  xTicksFontSize: 12,
  xTicksFont: 'Inter',
  labelForcedLength: labelMaxLength,
  yMinTicksLimit: 7,
  yMaxTicksLimit: 10,
  yStepSize: 1000000
})
const hardCostsStackedBarOptions = computed(() => {
  let json = hardStackedBarJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    title: function (context) {
      // return the full length version of the labels
      return hcStackedBarLabels.value[context[0].dataIndex]
    },
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      return label + ": " + currencyFormat(value);
    },
    footer: function (context) {
      const index = context[0].dataIndex;
      if (hcOriginalBudgetArr.value[index] === 0) {
        return "Variance: ∞ (Original budget was $0)";
      }
      let variance = (hcRevisedBudgetArr.value[index] - hcOriginalBudgetArr.value[index]) / hcOriginalBudgetArr.value[index]
      return (
        "Variance: " + percentFormat(variance)
      );
    },
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        const valIndex = (element) => element === val;
        // get index based on val
        let index = hcBudgetVarianceArr.value?.findIndex(valIndex)
        // only proceed if match is found
        if (index >= 0) {
          if (hcOriginalBudgetArr.value[index] === 0) {
            return "∞";
          }
          const variance = hcOriginalBudgetArr.value[index] > 0 ? (hcRevisedBudgetArr.value[index] - hcOriginalBudgetArr.value[index]) / hcOriginalBudgetArr.value[index] : 0
          const roundedPercentage = Math.round(variance * 100)
          return `${roundedPercentage.toFixed(0)}%`
        } else {
          return ''
        }
      } else {
        return ''
      }
    }
  }
  
  // Add onClick event handler
  json.onClick = function(event, elements) {
    if (!elements || elements.length === 0) return;
    
    const element = elements[0];
    const index = element.index;
    const datasetIndex = element.datasetIndex;
    
    // Only handle clicks on the budget variance dataset (index 1)
    if (datasetIndex === 1) {
      const lineItemName = hcStackedBarLabels.value[index];
      highlightGridRow(lineItemName, 'hardCost');
    }
  };
  
  return json
})

// Soft Costs
// Soft Costs Summary
const softCostsSummaryStackedBarData = computed(() => {
  // Do not show a negative variance on the chart as the chart looks really funky
  // adjusted sov - original sov (delta value)
  let changeInBudget = scTotalVariance.value
  if (changeInBudget < 0) {
    changeInBudget = 0
  }
  return {
    labels: ["Total"],
    datasets: [
      {
        label: "Original Budget",
        backgroundColor: "#4A8FD0", // $mediumBlue1
        data: [scTotalOriginalBudget.value], // original sov
      },
      {
        label: "Budget Variance",
        backgroundColor: "#84CADE", // $lightBlue1
        data: [changeInBudget],
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let softSummaryJsonConfig = standardStackedBarChartJson({
  barPercentage: 1,
  xTicksFontSize: 12,
  xTicksFont: 'Inter-Bold',
  yMaxTicksLimit: 10,
})
const softCostsSummaryStackedBarOptions = computed(() => {
  let json = softSummaryJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      let multilineTooltip = [label + ":"];
      multilineTooltip.push(currencyFormat(value));
      return multilineTooltip;
    },
    footer: function (context) {
      const index = context[0].dataIndex;
      if (scTotalOriginalBudget.value === 0) {
        return "Variance: ∞ (Original budget was $0)";
      }
      return (
        "Variance: " +
        percentFormat(scVariancePercent.value
        )
      );
    },
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        if (scTotalOriginalBudget.value === 0) {
          return "∞";
        }
        return `${Math.round(parseFloat(scVariancePercent.value).toFixed(2) * 100)}%`
      } else {
        return ''
      }
    }
  }
  
  // Add onClick event handler for the summary chart
  json.onClick = function(event, elements) {
    if (!elements || elements.length === 0) return;
    
    // When clicking on the summary chart, we'll scroll to the Soft Costs section in the grid
    const softCostsSection = document.querySelector('.soft-costs-container');
    if (softCostsSection) {
      softCostsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Show a notification
      showNotification("Soft Costs Summary", "softCost");
    }
  };
  
  return json
})
// Soft Costs Top Offenders
const softCostsStackedBarData = computed(() => {
  return {
    labels: standardizeLabelsLength(scStackedBarLabels.value),
    datasets: [
      {
        label: "Original Budget",
        backgroundColor: "#4A8FD0", // soft costs color based on figma
        data: scOriginalBudgetArr.value, // original sov
      },
      {
        label: "Budget Variance",
        backgroundColor: "#84C9DE", // NOTE : color variable doesn't work here
        data: scBudgetVarianceArr.value, // adjusted sov - original sov (delta value)
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let softStackedBarJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.8,
  xTicksFontSize: 12,
  xTicksFont: 'Inter',
  labelForcedLength: labelMaxLength,
  yMinTicksLimit: 7,
  yMaxTicksLimit: 10,
  yStepSize: 1000000
})
const softCostsStackedBarOptions = computed(() => {
  let json = softStackedBarJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    title: function (context) {
      // return the full length version of the labels
      return scStackedBarLabels.value[context[0].dataIndex]
    },
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      return label + ": " + currencyFormat(value);
    },
    footer: function (context) {
      const index = context[0].dataIndex;
      if (scOriginalBudgetArr.value[index] === 0) {
        return "Variance: ∞ (Original budget was $0)";
      }
      let variance = (scRevisedBudgetArr.value[index] - scOriginalBudgetArr.value[index]) / scOriginalBudgetArr.value[index]
      return (
        "Variance: " + percentFormat(variance)
      );
    },
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        const valIndex = (element) => element === val;
        // get index based on val
        let index = scBudgetVarianceArr.value?.findIndex(valIndex)
        // only proceed if match is found
        if (index >= 0) {
          if (scOriginalBudgetArr.value[index] === 0) {
            return "∞";
          }
          const variance = scOriginalBudgetArr.value[index] > 0 ? (scRevisedBudgetArr.value[index] - scOriginalBudgetArr.value[index]) / scOriginalBudgetArr.value[index] : 0
          const roundedPercentage = Math.round(variance * 100)
          return `${roundedPercentage.toFixed(0)}%`
        } else {
          return ''
        }
      } else {
        return ''
      }
    }
  }
  
  // Add onClick event handler
  json.onClick = function(event, elements) {
    if (!elements || elements.length === 0) return;
    
    const element = elements[0];
    const index = element.index;
    const datasetIndex = element.datasetIndex;
    
    // Only handle clicks on the budget variance dataset (index 1)
    if (datasetIndex === 1) {
      const lineItemName = scStackedBarLabels.value[index];
      highlightGridRow(lineItemName, 'softCost');
    }
  };
  
  return json
});

const selectedProjects = ref(compSetStore.selectedProjects);
const escalationQuery = ref(compSetStore.getEscalation ? `&escalation=${compSetStore.getEscalation}` : '')

compSetStore.$subscribe(syncProjects)
const currentProjects = ref(compSetStore.getIncludedProjects)
let avgTotalGSF = ref(mathSummaryStore.getAvgTotalGSF)
let avgTotalUnits = ref(mathSummaryStore.getAvgTotalUnits)
async function syncProjects() {
  escalationQuery.value = compSetStore.getEscalation ? `&escalation=${compSetStore.getEscalation}` : ''
  const storedProjects = compSetStore.getIncludedProjects
  compSetCount.value = Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).length
  if (compSetCount.value === 0) {
    withSelectedProject.value = false
  } else {
    withSelectedProject.value = true
  }
  currentProjects.value = storedProjects

  // await calculateHCSCAvgSummaryCostsPerGSF()
  await adjustAverages()
  avgTotalGSF = ref(mathSummaryStore.getAvgTotalGSF) //always set on any action to ensure data is there for the donut
  avgTotalUnits = ref(mathSummaryStore.getAvgTotalUnits)
  //resetChartData()
  setViewMode()
}

const errorStore = useErrorStore();
let hcTotalOriginalBudget = ref(0);
let hcTotalRevisedBudget = ref(0);
let scTotalOriginalBudget = ref(0);
let scTotalRevisedBudget = ref(0);
let miscFinancingTotalOriginalBudget = ref(0);
let miscFinancingTotalRevisedBudget = ref(0);
let lcTotalOriginalBudget = ref(0);
let lcTotalRevisedBudget = ref(0);
let contingenciesTotalOriginalBudget = ref(0);
let contingenciesTotalRevisedBudget = ref(0);

let lcTotalVariance = ref(0);
let hcTotalVariance = ref(0);
let scTotalVariance = ref(0);
let miscFinancingTotalVariance = ref(0);
let contingenciesTotalVariance = ref(0);
let lcVariancePercent = ref(0);
let hcVariancePercent = ref(0);
let scVariancePercent = ref(0);
let miscFinancingVariancePercent = ref(0);
let contingenciesVariancePercent = ref(0);

let hcStackedBarLabels = ref([]);
let hcOriginalBudgetArr = ref([]);
let hcRevisedBudgetArr = ref([]);
let hcBudgetVarianceArr = ref([]);
let hcBudgetIncPercArr = ref([]);

let scStackedBarLabels = ref([]);
let scOriginalBudgetArr = ref([]);
let scRevisedBudgetArr = ref([]);
let scBudgetVarianceArr = ref([]);
let scBudgetIncPercArr = ref([]);
let doughnutChartPercentage = ref([]);

// 🔥 Store raw (undivided) totals for donut chart recalculation on view mode change
let rawDoughnutTotals = ref({
  lc: 0,
  hc: 0,
  sc: 0,
  miscFinancing: 0,
  contingencies: 0
});

// 🔥 Update donut chart when view mode (Total/GSF/Unit) changes
function updateDoughnutChartData(divisor = 1) {
  const safeDivisor = divisor || 1;
  
  console.log('🍩 updateDoughnutChartData called with divisor:', safeDivisor);
  console.log('🍩 rawDoughnutTotals:', JSON.stringify(rawDoughnutTotals.value));
  
  // 🔥 UPDATE LEGEND VALUES with divisor
  lcTotalRevisedBudget.value = rawDoughnutTotals.value.lc / safeDivisor;
  hcTotalRevisedBudget.value = rawDoughnutTotals.value.hc / safeDivisor;
  scTotalRevisedBudget.value = rawDoughnutTotals.value.sc / safeDivisor;
  miscFinancingTotalRevisedBudget.value = rawDoughnutTotals.value.miscFinancing / safeDivisor;
  contingenciesTotalRevisedBudget.value = rawDoughnutTotals.value.contingencies / safeDivisor;
  
  // Recalculate donut chart data with new divisor
  const newDoughnutData = [
    rawDoughnutTotals.value.lc / safeDivisor,
    rawDoughnutTotals.value.hc / safeDivisor,
    rawDoughnutTotals.value.sc / safeDivisor,
    rawDoughnutTotals.value.miscFinancing / safeDivisor,
    rawDoughnutTotals.value.contingencies / safeDivisor
  ];
  
  console.log('🍩 newDoughnutData calculated:', newDoughnutData);
  console.log('🍩 Legend values updated:', {
    lc: lcTotalRevisedBudget.value,
    hc: hcTotalRevisedBudget.value,
    sc: scTotalRevisedBudget.value,
    miscFinancing: miscFinancingTotalRevisedBudget.value,
    contingencies: contingenciesTotalRevisedBudget.value
  });
  
  doughnutChartData.value = newDoughnutData;
  
  // Recalculate percentages
  const totalSumOfDoughnut = newDoughnutData.reduce((a, b) => (a || 0) + (b || 0), 0);
  doughnutChartPercentage.value = [];
  if (totalSumOfDoughnut > 0) {
    newDoughnutData.forEach(val => {
      const percentage = (val || 0) * 100 / totalSumOfDoughnut;
      const roundedPercentage = Math.round(percentage);
      doughnutChartPercentage.value.push(`${roundedPercentage}%`);
    });
  } else {
    doughnutChartPercentage.value = ['0%', '0%', '0%', '0%', '0%'];
  }
  
  console.log('🍩 Donut updated - divisor:', safeDivisor, 'data:', newDoughnutData, 'percentages:', doughnutChartPercentage.value);
  
  // Force chart refresh
  if (doughnutChartRef.value?.chart) {
    doughnutChartRef.value.chart.update();
    console.log('🍩 Chart.update() called');
  } else {
    console.warn('🍩 Chart ref not available for update');
  }
}

budgetDataStore.$subscribe(() => setChartData());
function setChartData(emittedData = null) {
  // 🔧 FIX: Accept emitted data from BudgetGrid and store it
  if (emittedData && Array.isArray(emittedData) && emittedData.length > 0) {
    budgetDataStore.setAggregatedBudgetChartData(emittedData);
  }
  let chartData = budgetDataStore.getAggregatedBudgetChartData
  // These group ids are constants
  // These should only change if we renamed the parent terms
  const ACQUISITION_GROUP_KEY = "ACQUISITION COSTS"
  const HC_GROUP_KEY = "HARD COSTS"
  const SC_GROUP_KEY = "SOFT COSTS"
  const FINANCING_GROUP_KEY = "FINANCING COSTS"
  const CONTINGENCIES_GROUP_KEY = "Owner Contingencies"

  // build doughnut data
  // check first if chart data is non null
  if (chartData) {
    let acquisitionGroupData = chartData.find(row => row.key === ACQUISITION_GROUP_KEY)
    let hcGroupData = chartData.find(row => row.key === HC_GROUP_KEY)
    let scGroupData = chartData.find(row => row.key === SC_GROUP_KEY)
    let financingGroupData = chartData.find(row => row.key === FINANCING_GROUP_KEY)
    let contingenciesGroupData = chartData.find(row => row.key === CONTINGENCIES_GROUP_KEY)

    // 🔥 FIX: If category totals not found by exact key, SUM items by category_name
    // This handles comp set summary data where we have individual DDA term rows
    const sumByCategory = (categoryName, field) => {
      return chartData
        .filter(row => {
          const cat = (row.category_name || '').toUpperCase();
          const key = (row.key || '').toUpperCase();
          // Don't double-count if we find the actual total row
          if (key.includes('TOTAL')) return false;
          return cat.includes(categoryName.toUpperCase());
        })
        .reduce((sum, row) => sum + (parseFloat(row.aggData?.[field]) || 0), 0);
    };
    
    // Use calculated values if available, otherwise use the original data
    if (useCalculatedValues.value && viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_TOTAL) {
      lcTotalRevisedBudget.value = calculatedLCTotalRevisedBudget.value
      hcTotalRevisedBudget.value = calculatedHCTotalRevisedBudget.value
      scTotalRevisedBudget.value = calculatedSCTotalRevisedBudget.value
      miscFinancingTotalRevisedBudget.value = calculatedMiscFinancingTotalRevisedBudget.value
      contingenciesTotalRevisedBudget.value = calculatedContingenciesTotalRevisedBudget.value
      
      lcTotalOriginalBudget.value = acquisitionGroupData ? acquisitionGroupData['aggData']['original_budget'] : 0
      hcTotalOriginalBudget.value = calculatedHCTotalOriginalBudget.value
      scTotalOriginalBudget.value = calculatedSCTotalOriginalBudget.value
      miscFinancingTotalOriginalBudget.value = financingGroupData ? financingGroupData['aggData']['original_budget'] : 0
      contingenciesTotalOriginalBudget.value = contingenciesGroupData ? contingenciesGroupData['aggData']['original_budget'] : 0
    } else {
      // 🔥 FIX: Use exact match if found, otherwise SUM all items in that category
      lcTotalRevisedBudget.value = acquisitionGroupData 
        ? acquisitionGroupData['aggData']['current_budget'] 
        : sumByCategory('ACQUISITION', 'current_budget')
      hcTotalRevisedBudget.value = hcGroupData 
        ? hcGroupData['aggData']['current_budget'] 
        : sumByCategory('HARD', 'current_budget')
      scTotalRevisedBudget.value = scGroupData 
        ? scGroupData['aggData']['current_budget'] 
        : sumByCategory('SOFT', 'current_budget')
      miscFinancingTotalRevisedBudget.value = financingGroupData 
        ? financingGroupData['aggData']['current_budget'] 
        : sumByCategory('FINANCING', 'current_budget')
      contingenciesTotalRevisedBudget.value = contingenciesGroupData 
        ? contingenciesGroupData['aggData']['current_budget'] 
        : sumByCategory('CONTINGENC', 'current_budget')

      lcTotalOriginalBudget.value = acquisitionGroupData 
        ? acquisitionGroupData['aggData']['original_budget'] 
        : sumByCategory('ACQUISITION', 'original_budget')
      hcTotalOriginalBudget.value = hcGroupData 
        ? hcGroupData['aggData']['original_budget'] 
        : sumByCategory('HARD', 'original_budget')
      scTotalOriginalBudget.value = scGroupData 
        ? scGroupData['aggData']['original_budget'] 
        : sumByCategory('SOFT', 'original_budget')
      miscFinancingTotalOriginalBudget.value = financingGroupData 
        ? financingGroupData['aggData']['original_budget'] 
        : sumByCategory('FINANCING', 'original_budget')
      contingenciesTotalOriginalBudget.value = contingenciesGroupData 
        ? contingenciesGroupData['aggData']['original_budget'] 
        : sumByCategory('CONTINGENC', 'original_budget')
    }
    
    console.log('🍩 Category totals (after fix):', {
      acquisition: lcTotalRevisedBudget.value,
      hardCosts: hcTotalRevisedBudget.value,
      softCosts: scTotalRevisedBudget.value,
      financing: miscFinancingTotalRevisedBudget.value,
      contingencies: contingenciesTotalRevisedBudget.value
    });

    // 🔥 FIX: Calculate per view mode - logic was backwards!
    // VIEW_BY_GSF: divide by avgTotalGSF
    if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) {
      hcAvgOriginalCostPerGSF.value = hcTotalOriginalBudget.value / (avgTotalGSF.value || 1)
      hcAvgRevisedCostPerGSF.value = hcTotalRevisedBudget.value / (avgTotalGSF.value || 1)
      scAvgOriginalCostPerGSF.value = scTotalOriginalBudget.value / (avgTotalGSF.value || 1)
      scAvgRevisedCostPerGSF.value = scTotalRevisedBudget.value / (avgTotalGSF.value || 1)
    }
    // VIEW_BY_UNIT: divide by avgTotalUnits
    else if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) {
      hcAvgOriginalCostPerGSF.value = hcTotalOriginalBudget.value / (avgTotalUnits.value || 1)
      hcAvgRevisedCostPerGSF.value = hcTotalRevisedBudget.value / (avgTotalUnits.value || 1)
      scAvgOriginalCostPerGSF.value = scTotalOriginalBudget.value / (avgTotalUnits.value || 1)
      scAvgRevisedCostPerGSF.value = scTotalRevisedBudget.value / (avgTotalUnits.value || 1)
    }
    // VIEW_BY_TOTAL (default): use raw total values, no division
    else {
      hcAvgOriginalCostPerGSF.value = hcTotalOriginalBudget.value
      hcAvgRevisedCostPerGSF.value = hcTotalRevisedBudget.value
      scAvgOriginalCostPerGSF.value = scTotalOriginalBudget.value
      scAvgRevisedCostPerGSF.value = scTotalRevisedBudget.value
    }
    
    isHCSCCostPerGSFDataLoaded.value = true

    // Calculate variances based on whether we're using calculated values or not
    if (useCalculatedValues.value && viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_TOTAL) {
      // Calculate variances for calculated values
      lcTotalVariance.value = lcTotalRevisedBudget.value - lcTotalOriginalBudget.value
      hcTotalVariance.value = hcTotalRevisedBudget.value - hcTotalOriginalBudget.value
      scTotalVariance.value = scTotalRevisedBudget.value - scTotalOriginalBudget.value
      miscFinancingTotalVariance.value = miscFinancingTotalRevisedBudget.value - miscFinancingTotalOriginalBudget.value
      contingenciesTotalVariance.value = contingenciesTotalRevisedBudget.value - contingenciesTotalOriginalBudget.value
      
      // Calculate variance percentages
      lcVariancePercent.value = lcTotalOriginalBudget.value !== 0 ? lcTotalVariance.value / lcTotalOriginalBudget.value : 0
      hcVariancePercent.value = hcTotalOriginalBudget.value !== 0 ? hcTotalVariance.value / hcTotalOriginalBudget.value : 0
      scVariancePercent.value = scTotalOriginalBudget.value !== 0 ? scTotalVariance.value / scTotalOriginalBudget.value : 0
      miscFinancingVariancePercent.value = miscFinancingTotalOriginalBudget.value !== 0 ? miscFinancingTotalVariance.value / miscFinancingTotalOriginalBudget.value : 0
      contingenciesVariancePercent.value = contingenciesTotalOriginalBudget.value !== 0 ? contingenciesTotalVariance.value / contingenciesTotalOriginalBudget.value : 0
    } else {
      // 🔧 FIX: Calculate variance from budget values instead of relying on missing total_adjustments field
      lcTotalVariance.value = lcTotalRevisedBudget.value - lcTotalOriginalBudget.value
      hcTotalVariance.value = hcTotalRevisedBudget.value - hcTotalOriginalBudget.value
      scTotalVariance.value = scTotalRevisedBudget.value - scTotalOriginalBudget.value
      miscFinancingTotalVariance.value = miscFinancingTotalRevisedBudget.value - miscFinancingTotalOriginalBudget.value
      contingenciesTotalVariance.value = contingenciesTotalRevisedBudget.value - contingenciesTotalOriginalBudget.value

      // Calculate variance percentages
      lcVariancePercent.value = lcTotalOriginalBudget.value !== 0 ? lcTotalVariance.value / lcTotalOriginalBudget.value : 0
      hcVariancePercent.value = hcTotalOriginalBudget.value !== 0 ? hcTotalVariance.value / hcTotalOriginalBudget.value : 0
      scVariancePercent.value = scTotalOriginalBudget.value !== 0 ? scTotalVariance.value / scTotalOriginalBudget.value : 0
      miscFinancingVariancePercent.value = miscFinancingTotalOriginalBudget.value !== 0 ? miscFinancingTotalVariance.value / miscFinancingTotalOriginalBudget.value : 0
      contingenciesVariancePercent.value = contingenciesTotalOriginalBudget.value !== 0 ? contingenciesTotalVariance.value / contingenciesTotalOriginalBudget.value : 0
    }

    // 🔥 Store raw totals for view mode recalculation
    rawDoughnutTotals.value = {
      lc: parseFloat(lcTotalRevisedBudget.value) || 0,
      hc: parseFloat(hcTotalRevisedBudget.value) || 0,
      sc: parseFloat(scTotalRevisedBudget.value) || 0,
      miscFinancing: parseFloat(miscFinancingTotalRevisedBudget.value) || 0,
      contingencies: parseFloat(contingenciesTotalRevisedBudget.value) || 0
    };
    
    // 🔥 Apply current divisor to the raw totals
    const currentDivisor = mathSummaryStore.getViewModeDivisor || 1;
    updateDoughnutChartData(currentDivisor);
    
    // REMOVED - updateDoughnutChartData now handles all donut chart updates
    // (doughnutChartData, percentages, legend values all set by updateDoughnutChartData)


    // 🔧 FIX: Handle BOTH AG Grid structured data (with allLeafChildren) AND flat data
    const getVariance = (row) => {
      const current = row?.data?.current_budget || row?.aggData?.current_budget || row?.current_budget || 0;
      const original = row?.data?.original_budget || row?.aggData?.original_budget || row?.original_budget || 0;
      return current - original;
    };
    
    const getKey = (row) => {
      return row?.key || row?.description || row?.scope_detail || row?.dda_term_name || '';
    };
    
    const isHardCost = (row) => {
      const hierarchy = row?.hierarchy_path || row?.itemHierarchy || [];
      const category = row?.category_name || '';
      const key = row?.key || '';
      return hierarchy.some(h => h?.toUpperCase?.()?.includes('HARD')) || 
             category.toUpperCase().includes('HARD') ||
             key.toUpperCase() === 'HARD COSTS';
    };
    
    const isSoftCost = (row) => {
      const hierarchy = row?.hierarchy_path || row?.itemHierarchy || [];
      const category = row?.category_name || '';
      const key = row?.key || '';
      return hierarchy.some(h => h?.toUpperCase?.()?.includes('SOFT')) || 
             category.toUpperCase().includes('SOFT') ||
             key.toUpperCase() === 'SOFT COSTS';
    };
    
    // Check if we have AG Grid structured data or flat data
    let hcItems = [];
    let scItems = [];
    
    if (hcGroupData?.allLeafChildren?.length > 0) {
      // AG Grid structured data
      hcItems = hcGroupData.allLeafChildren;
    } else {
      // Flat data - filter by category/hierarchy
      hcItems = chartData.filter(row => {
        const key = getKey(row);
        // Exclude category headers and totals
        if (key.toUpperCase() === 'HARD COSTS' || key.toUpperCase().includes('TOTAL')) return false;
        return isHardCost(row);
      });
    }
    
    if (scGroupData?.allLeafChildren?.length > 0) {
      // AG Grid structured data
      scItems = scGroupData.allLeafChildren;
    } else {
      // Flat data - filter by category/hierarchy
      scItems = chartData.filter(row => {
        const key = getKey(row);
        // Exclude category headers and totals
        if (key.toUpperCase() === 'SOFT COSTS' || key.toUpperCase().includes('TOTAL')) return false;
        return isSoftCost(row);
      });
    }
    
    // Sort by variance (highest first)
    const sortedHCGroupData = [...hcItems].sort((a, b) => getVariance(b) - getVariance(a));
    const sortedSCGroupData = [...scItems].sort((a, b) => getVariance(b) - getVariance(a));

    // Get the top 5 with positive variance
    const top5DataHC = [];
    const top5DataSC = [];

    // Loop through the sorted array for hard cost
    for (const obj of sortedHCGroupData) {
      const key = getKey(obj).toLowerCase();
      const variance = getVariance(obj);
      // Skip if it's a trade category or has no positive variance
      // 🔥 Also skip category headers like "hard cost" or "hard costs"
      if (variance <= 0 || key.includes("trade") || key.includes("total") || key === "hard cost" || key === "hard costs") continue;
      
      top5DataHC.push(obj);
      if (top5DataHC.length === 5) break;
    }

    // Loop through the sorted array for soft cost
    for (const obj of sortedSCGroupData) {
      const key = getKey(obj).toLowerCase();
      const variance = getVariance(obj);
      // Skip if it has excluded terms or no positive variance
      // 🔥 Also skip category headers like "soft cost" or "soft costs"
      if (variance <= 0 || key.includes("carrying") || key.includes("land lease") || key.includes("total") || key === "soft cost" || key === "soft costs") continue;
      
      top5DataSC.push(obj);
      if (top5DataSC.length === 5) break;
    }
    
    console.log(`📊 Top offenders: HC=${top5DataHC.length} items, SC=${top5DataSC.length} items`);

    // HCTopOffenders - Build the chart data
    hcStackedBarLabels.value = [];
    hcOriginalBudgetArr.value = [];
    hcRevisedBudgetArr.value = [];
    hcBudgetVarianceArr.value = [];
    hcBudgetIncPercArr.value = [];
    top5DataHC.forEach((row) => {
      // Handle both AG Grid structured data and flat data
      const original = row.data?.original_budget || row.aggData?.original_budget || row.original_budget || 0;
      const current = row.data?.current_budget || row.aggData?.current_budget || row.current_budget || 0;
      const variance = current - original;
      const label = getKey(row);
      
      hcStackedBarLabels.value.push(label);
      hcOriginalBudgetArr.value.push(original);
      hcRevisedBudgetArr.value.push(current);
      hcBudgetVarianceArr.value.push(variance);
      const variancePercent = original > 0 ? variance / original : 0;
      hcBudgetIncPercArr.value.push(variancePercent);
    });
    
    // SCTopOffenders - Build the chart data
    scStackedBarLabels.value = [];
    scOriginalBudgetArr.value = [];
    scRevisedBudgetArr.value = [];
    scBudgetVarianceArr.value = [];
    scBudgetIncPercArr.value = [];
    top5DataSC.forEach((row) => {
      // Handle both AG Grid structured data and flat data
      const original = row.data?.original_budget || row.aggData?.original_budget || row.original_budget || 0;
      const current = row.data?.current_budget || row.aggData?.current_budget || row.current_budget || 0;
      const variance = current - original;
      const label = getKey(row);
      
      scStackedBarLabels.value.push(label);
      scOriginalBudgetArr.value.push(original);
      scRevisedBudgetArr.value.push(current);
      scBudgetVarianceArr.value.push(variance);
      const variancePercent = original > 0 ? variance / original : 0;
      scBudgetIncPercArr.value.push(variancePercent);
    });
    
    // 🔥 FIX: Force chart update after data changes
    console.log('🔥 BudgetDetails: Forcing chart updates for top 5 offenders');
    if (hcOffendersChartRef.value?.chart) {
      hcOffendersChartRef.value.chart.update();
    }
    if (scOffendersChartRef.value?.chart) {
      scOffendersChartRef.value.chart.update();
    }
    
    // 🔥 FIX: Force doughnut chart update so center total text updates
    if (doughnutChartRef.value?.chart) {
      console.log('🍩 Forcing doughnut chart update - total:', totalRevisedBudget.value);
      doughnutChartRef.value.chart.update();
    }
  }
}

//TODO: ASk Jam about this and it's relation to calculateHCSCAvgSummaryCostsPerGSF
let hcSummaryAvgOriginal = computed(() => {
  return hcAvgOriginalCostPerGSF.value
})
let hcSummaryAvgRevised = computed(() => {
  return hcAvgRevisedCostPerGSF.value
})
let scSummaryAvgOriginal = computed(() => {
  return scAvgOriginalCostPerGSF.value
})
let scSummaryAvgRevised = computed(() => {
  return scAvgRevisedCostPerGSF.value
})
let hcAvgOriginalCostPerGSF = ref(0)
let hcAvgRevisedCostPerGSF = ref(0)
let scAvgOriginalCostPerGSF = ref(0)
let scAvgRevisedCostPerGSF = ref(0)
let isHCSCCostPerGSFDataLoaded = ref(false)

// Updated implementation to ensure $ / gsf is aligned with grid values
// function calculateHCSCAvgSummaryCostsPerGSF() {
//   isHCSCCostPerGSFDataLoaded.value = false
//   const selectedIds = Object.keys(selectedProjects.value).filter(id => selectedProjects.value[id]).join(',')
//   let HCTotalOriginalCostPerGSF = 0
//   let HCTotalRevisedCostPerGSF = 0
//   let SCTotalOriginalCostPerGSF = 0
//   let SCTotalRevisedCostPerGSF = 0
//
//   if (selectedIds) {
//     axiosQuery(
//       `${apiBase}mvw_most_recent_by_projects/fetch_hc_sc_costs_per_project?project_ids=${selectedIds}${escalationQuery.value}`,
//       authToken,
//       (data) => {
//         const validIds = selectedIds.split(',')
//         let hardCostsData = data.hard
//         let softCostsData = data.soft
//         validIds.forEach((id) => {
//           HCTotalOriginalCostPerGSF += parseFloat(hardCostsData[id]['total_original'] || 0)
//           HCTotalRevisedCostPerGSF += parseFloat(hardCostsData[id]['total_revised'] || 0)
//           SCTotalOriginalCostPerGSF += parseFloat(softCostsData[id]['total_original'] || 0)
//           SCTotalRevisedCostPerGSF += parseFloat(softCostsData[id]['total_revised'] || 0)
//         })
//
//         // Get average costs
//         hcAvgOriginalCostPerGSF.value = HCTotalOriginalCostPerGSF / validIds.length
//         hcAvgRevisedCostPerGSF.value = HCTotalRevisedCostPerGSF / validIds.length
//         scAvgOriginalCostPerGSF.value = SCTotalOriginalCostPerGSF / validIds.length
//         scAvgRevisedCostPerGSF.value = SCTotalRevisedCostPerGSF / validIds.length
//
//         // Get avg costs / avg GSF
//         hcAvgOriginalCostPerGSF.value = hcAvgOriginalCostPerGSF.value / ( avgTotalGSF.value || 1)
//         hcAvgRevisedCostPerGSF.value = hcAvgRevisedCostPerGSF.value / (avgTotalGSF.value || 1)
//         scAvgOriginalCostPerGSF.value = scAvgOriginalCostPerGSF.value / (avgTotalGSF.value || 1)
//         scAvgRevisedCostPerGSF.value = scAvgRevisedCostPerGSF.value / (avgTotalGSF.value || 1)
//       },
//       errorStore
//     );
//   }
//   isHCSCCostPerGSFDataLoaded.value = true
//
// }

// Old implementation
// At some point we might need to revert to this calculation

// function calculateHCSCAvgSummaryCostsPerGSF() {
//   isHCSCCostPerGSFDataLoaded.value = false
//   const selectedIds = Object.keys(selectedProjects.value).filter(id => selectedProjects.value[id]).join(',')
//   let HCTotalOriginalCostPerGSF = 0
//   let HCTotalRevisedCostPerGSF = 0
//   let SCTotalOriginalCostPerGSF = 0
//   let SCTotalRevisedCostPerGSF = 0

//   if (selectedIds) {
//     axiosQuery(
//       `${apiBase}mvw_most_recent_by_projects/fetch_hc_sc_costs_per_project?project_ids=${selectedIds}${escalationQuery.value}`,
//       authToken,
//       (data) => {
//         const validIds = selectedIds.split(',')
//         let hardCostsData = data.hard
//         let softCostsData = data.soft
//         validIds.forEach((id) => {
//           let projectGSF = parseFloat(compSetStore.getPerProjectGSF[id] || 0)
//           HCTotalOriginalCostPerGSF += parseFloat(hardCostsData[id]['total_original'] || 0) / (projectGSF || 1)
//           HCTotalRevisedCostPerGSF += parseFloat(hardCostsData[id]['total_revised'] || 0) / (projectGSF || 1)
//           SCTotalOriginalCostPerGSF += parseFloat(softCostsData[id]['total_original'] || 0) / (projectGSF || 1)
//           SCTotalRevisedCostPerGSF += parseFloat(softCostsData[id]['total_revised'] || 0) / (projectGSF || 1)
//         })
//         hcAvgOriginalCostPerGSF.value = HCTotalOriginalCostPerGSF / validIds.length
//         hcAvgRevisedCostPerGSF.value = HCTotalRevisedCostPerGSF / validIds.length
//         scAvgOriginalCostPerGSF.value = SCTotalOriginalCostPerGSF / validIds.length
//         scAvgRevisedCostPerGSF.value = SCTotalRevisedCostPerGSF / validIds.length

//       },
//       errorStore
//     );
//   }
//   isHCSCCostPerGSFDataLoaded.value = true

// }

let viewByTGU = ref(mathSummaryStore.getViewModeBy || VIEW_MODES_TGU.VIEW_BY_TOTAL);
let viewModeLabel=ref('')
let subjectPropertySqFt = ref('')
let originalViewByGSFValues = {}

function setViewMode() {
    mathSummaryStore.setViewModeBy(viewByTGU.value)
    let divisor = 1;
    
    if (viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_GSF) {
      mathSummaryStore.setViewModeBy(VIEW_MODES_TGU.VIEW_BY_GSF);
      divisor = avgTotalGSF.value || 1;
      mathSummaryStore.setViewModeDivisor(divisor)
      viewModeLabel.value='/gsf'
      doughnutViewMode.value = 'gsf'
      // Reset subject property values when switching to GSF view
      originalViewByGSFValues = {}
    } else if (viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_UNIT) {
      mathSummaryStore.setViewModeBy(VIEW_MODES_TGU.VIEW_BY_UNIT);
      divisor = avgTotalUnits.value || 1;
      mathSummaryStore.setViewModeDivisor(divisor)
      viewModeLabel.value='/unit'
      doughnutViewMode.value = 'unit'
      // Reset subject property values when switching to Unit view
      originalViewByGSFValues = {}
    } else {
      mathSummaryStore.setViewModeBy(VIEW_MODES_TGU.VIEW_BY_TOTAL);
      mathSummaryStore.setViewModeDivisor(1)
      viewModeLabel.value=''
      doughnutViewMode.value = 'total'
      divisor = 1;
      
      // If switching to VIEW_BY_TOTAL and we have a subject property square feet value,
      // recalculate the values
      if (subjectPropertySqFt.value) {
        updateSubjectPropertyValues()
      }
    }
    
    // 🔥 FIX: Update donut chart data with new divisor
    updateDoughnutChartData(divisor);
}

// The function that gets called on change
function updateSubjectPropertyValues(event) {
  // Prevent default form submission
  if (event) {
    event.preventDefault();
  }
  
  // Parse the input value as a number
  const sqFtValue = parseFloat(subjectPropertySqFt.value);
  
  // Only proceed if we have a valid number
  if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_TOTAL && !isNaN(sqFtValue) && sqFtValue > 0) {
    // Store original GSF values if not already stored
    if (Object.keys(originalViewByGSFValues).length === 0) {
      // Switch to GSF view temporarily to get the per square foot values
      const currentViewMode = viewByTGU.value;
      
      // Store the current values
      const tempViewMode = mathSummaryStore.getViewModeBy;
      const tempViewModeDivisor = mathSummaryStore.getViewModeDivisor;
      
      // Set to GSF view
      mathSummaryStore.setViewModeBy(VIEW_MODES_TGU.VIEW_BY_GSF);
      mathSummaryStore.setViewModeDivisor(avgTotalGSF.value);
      
      // Fetch the data with GSF values
      const escalationQuery = compSetStore.getEscalationQuery ? compSetStore.getEscalationQuery : '';
      
      try {
        axiosQuery(
          `${apiBase}mvw_most_recent_by_projects/leaves_sum_grouped_by_term?project_ids=${Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).join(',')}${escalationQuery}`,
          authToken.value,
          (data) => {
            // Process the data to get per GSF values
            const perGSFValues = processDataForGSF(data);
            
            // Store the GSF values
            originalViewByGSFValues = perGSFValues;
            
            // Switch back to total view
            mathSummaryStore.setViewModeBy(tempViewMode);
            mathSummaryStore.setViewModeDivisor(tempViewModeDivisor);
            
            // Calculate with the subject property square feet
            calculateWithSubjectProperty();
            
            // Force the BudgetGrid to update by refreshing the budget data store
            try {
              budgetDataStore.fetchBudgetData(
                Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).join(','),
                compSetStore.getEscalationQuery || ''
              );
            } catch (error) {
              console.error("Error updating budget data:", error);
            }
          },
          errorStore
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    } else {
      // Use the stored GSF values to calculate
      calculateWithSubjectProperty();
      
      // Force the BudgetGrid to update by refreshing the budget data store
      try {
        budgetDataStore.fetchBudgetData(
          Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).join(','),
          compSetStore.getEscalationQuery || ''
        );
      } catch (error) {
        console.error("Error updating budget data:", error);
      }
    }
  }
}

function processDataForGSF(data) {
  // Process the data to extract per GSF values
  let lcTotal = 0
  let hcTotal = 0
  let scTotal = 0
  let miscTotal = 0
  let contingenciesTotal = 0
  let hcOriginalTotal = 0
  let scOriginalTotal = 0
  
  // Group data by category
  data.forEach(item => {
    const hierarchy = item.itemHierarchy[0].toLowerCase()
    if (hierarchy.includes('acquisition')) {
      lcTotal += item.current_budget / avgTotalGSF.value
    } else if (hierarchy.includes('hard')) {
      hcTotal += item.current_budget / avgTotalGSF.value
      hcOriginalTotal += item.original_budget / avgTotalGSF.value
    } else if (hierarchy.includes('soft')) {
      scTotal += item.current_budget / avgTotalGSF.value
      scOriginalTotal += item.original_budget / avgTotalGSF.value
    } else if (hierarchy.includes('misc') || hierarchy.includes('financ')) {
      miscTotal += item.current_budget / avgTotalGSF.value
    } else if (hierarchy.includes('contingenc')) {
      contingenciesTotal += item.current_budget / avgTotalGSF.value
    }
  })
  
  return {
    lcTotalRevisedBudget: lcTotal,
    hcTotalRevisedBudget: hcTotal,
    scTotalRevisedBudget: scTotal,
    miscFinancingTotalRevisedBudget: miscTotal,
    contingenciesTotalRevisedBudget: contingenciesTotal,
    hcTotalOriginalBudget: hcOriginalTotal,
    scTotalOriginalBudget: scOriginalTotal
  }
}

// Create refs to store the calculated values
let calculatedLCTotalRevisedBudget = ref(0)
let calculatedHCTotalRevisedBudget = ref(0)
let calculatedSCTotalRevisedBudget = ref(0)
let calculatedMiscFinancingTotalRevisedBudget = ref(0)
let calculatedContingenciesTotalRevisedBudget = ref(0)
let calculatedHCTotalOriginalBudget = ref(0)
let calculatedSCTotalOriginalBudget = ref(0)
let useCalculatedValues = ref(false)

function calculateWithSubjectProperty() {
  const sqFt = parseFloat(subjectPropertySqFt.value)
  if (!isNaN(sqFt) && sqFt > 0) {
    // Calculate the new values based on square feet
    calculatedLCTotalRevisedBudget.value = originalViewByGSFValues.lcTotalRevisedBudget * sqFt
    calculatedHCTotalRevisedBudget.value = originalViewByGSFValues.hcTotalRevisedBudget * sqFt
    calculatedSCTotalRevisedBudget.value = originalViewByGSFValues.scTotalRevisedBudget * sqFt
    calculatedMiscFinancingTotalRevisedBudget.value = originalViewByGSFValues.miscFinancingTotalRevisedBudget * sqFt
    calculatedContingenciesTotalRevisedBudget.value = originalViewByGSFValues.contingenciesTotalRevisedBudget * sqFt
    calculatedHCTotalOriginalBudget.value = originalViewByGSFValues.hcTotalOriginalBudget * sqFt
    calculatedSCTotalOriginalBudget.value = originalViewByGSFValues.scTotalOriginalBudget * sqFt
    
    // Set flag to use calculated values
    useCalculatedValues.value = true
    
    // Store the subject property square feet in the math summary store
    mathSummaryStore.setSubjectPropertySqFt(sqFt)
    
    // Force chart data update
    setChartData()
  } else {
    // If input is invalid, revert to original values
    useCalculatedValues.value = false
    mathSummaryStore.setSubjectPropertySqFt(null)
    setChartData()
  }
}


let perProjectGSF = ref({})
// Adjust the Comp Set Averages
// Used to pre-store averages of active compset
const adjustAverages = () => {
    const newTotalGSF = [];
    const newNumUnits = []

    if (compSetStore.getIncludedProjects.length > 0) {
        for (const project of currentProjects.value) {
            // Check if the project is selected
            if (compSetStore.selectedProjects[project.id]) {
                // TODO : For now, we default to 1 if value for that property is non-existent
                newTotalGSF.push(project.total_gsf || 1);
                newNumUnits.push(project.total_num_units || 1);
                // Note : This calculation of per project GSF is not actively in use right now but might be reverted to later on
                perProjectGSF.value[project.id] = project.total_gsf || 1; 
            }
        }
    } 
    avgTotalGSF.value = getAverage(newTotalGSF);
    avgTotalUnits.value = getAverage(newNumUnits);
    mathSummaryStore.setAvgTotalGSF(avgTotalGSF.value)
    mathSummaryStore.setPerProjectGSF(perProjectGSF.value)
    mathSummaryStore.setAvgTotalUnits(avgTotalUnits.value)
}

// Calculate the average of two or more numbers inside an array
const getAverage = (numbers) => {
    // Calculate the sum of all the numbers
    const sum = numbers.reduce((acc, number) => acc + number, 0);
    // Calculate the average
    const average = sum / numbers.length;
    return average;
}

const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";
const authStore = useAuthStore();
let authToken = reactive({});
onMounted(() => {
  authToken.value = authStore.getAuthTokenHeader
  // Enable auth to load fully before updating data (not sure if this completely makes sense)
  setTimeout(() => {
    setChartData()
    // calculateHCSCAvgSummaryCostsPerGSF()
    //adjustAverages()
  }, 0)

  // Attach the function to the window object for Cypress
  window.getDoughnutChartInstance = getDoughnutChartInstance;
  window.getHCBarChartInstance = getHCBarChartInstance;
  window.getSCBarChartInstance = getSCBarChartInstance;
})

const doughnutChartRef = ref(null);
// Expose the chart instance for Cypress
const getDoughnutChartInstance = () => {
  return doughnutChartRef.value?.chart; // Access the chart instance via proxy
};

const hcBarChartRef = ref(null);
const scBarChartRef = ref(null);
const hcOffendersChartRef = ref(null);
const scOffendersChartRef = ref(null);

// Expose the bar chart instance for Cypress
const getHCBarChartInstance = () => {
  return hcBarChartRef.value?.chart; // Access the chart instance via proxy
};

const getSCBarChartInstance = () => {
  return scBarChartRef.value?.chart; // Access the chart instance via proxy
};

// Reference to the budget grid component
const budgetGridRef = ref(null);

/**
 * Highlights a row in the budget grid based on the line item name
 * @param {string} lineItemName - The name of the line item to highlight
 * @param {string} costType - The type of cost ('hardCost' or 'softCost')
 */
function highlightGridRow(lineItemName, costType) {
  // First try to use the DOM approach as a fallback
  const gridComponent = document.getElementById('budgetViewGrid');
  if (!gridComponent) return;
  
  // Find the ag-grid instance
  const agGridElement = gridComponent.querySelector('.ag-root-wrapper');
  if (!agGridElement) return;
  
  // Find all row elements
  const rowElements = agGridElement.querySelectorAll('.ag-row');
  
  // Clear any previous highlighting
  rowElements.forEach(row => {
    row.style.backgroundColor = '';
    row.style.transition = '';
  });
  
  // Find the row with the matching line item name
  let foundRow = null;
  rowElements.forEach(row => {
    const cellElements = row.querySelectorAll('.ag-cell');
    cellElements.forEach(cell => {
      if (cell.textContent && cell.textContent.trim() === lineItemName) {
        foundRow = row;
      }
    });
  });
  
  if (foundRow) {
    // Scroll the row into view
    foundRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Highlight the row with a color based on the cost type
    const highlightColor = costType === 'hardCost' ? 'rgba(74, 95, 213, 0.2)' : 'rgba(74, 143, 208, 0.2)';
    foundRow.style.backgroundColor = highlightColor;
    foundRow.style.transition = 'background-color 0.5s ease';
    
    // Flash effect to draw attention
    setTimeout(() => {
      foundRow.style.backgroundColor = costType === 'hardCost' ? 'rgba(74, 95, 213, 0.4)' : 'rgba(74, 143, 208, 0.4)';
      setTimeout(() => {
        foundRow.style.backgroundColor = highlightColor;
      }, 300);
    }, 300);
    
    // Clear the highlight after a few seconds
    setTimeout(() => {
      foundRow.style.backgroundColor = '';
    }, 5000);
    
    // Add a notification to the user
    showNotification(lineItemName, costType);
  }
}

/**
 * Shows a notification to the user about the selected line item
 * @param {string} lineItemName - The name of the line item
 * @param {string} costType - The type of cost ('hardCost' or 'softCost')
 */
function showNotification(lineItemName, costType) {
  // Create a notification element
  const notification = document.createElement('div');
  notification.className = 'chart-click-notification';
  notification.textContent = `Showing ${costType === 'hardCost' ? 'Hard Cost' : 'Soft Cost'}: ${lineItemName}`;
  
  // Style the notification
  notification.style.position = 'fixed';
  notification.style.bottom = '20px';
  notification.style.right = '20px';
  notification.style.backgroundColor = costType === 'hardCost' ? '#4A5FD5' : '#4A8FD0';
  notification.style.color = 'white';
  notification.style.padding = '10px 15px';
  notification.style.borderRadius = '4px';
  notification.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
  notification.style.zIndex = '9999';
  notification.style.opacity = '0';
  notification.style.transition = 'opacity 0.3s ease';
  
  // Add the notification to the document
  document.body.appendChild(notification);
  
  // Fade in
  setTimeout(() => {
    notification.style.opacity = '1';
  }, 10);
  
  // Remove after a few seconds
  setTimeout(() => {
    notification.style.opacity = '0';
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 300);
  }, 4000);
}
</script>
<style scoped lang="scss">
@import "./src/assets/main.scss";

.budget-details-container {
  display: flex;
  flex-direction: column;
}

.chart-container {
  display: flex;
  flex-direction: row;
  margin: 20px 30px 0px 10px;
  justify-content: space-between;
}

.doughnut-chart-container {
  padding: 20px;
  background: $white1;
  border: 1px solid $white1;
  box-shadow: 6px 0 18px rgba(0, 0, 0, 0.06);
  border-radius: 15px;
  margin-right: 1%;
  flex: 1;
}

.bar-chart-container {
  flex: 1 0;
  padding: 20px;
  background: $white1;
  border: 1px solid $white1;
  box-shadow: 6px 0 18px rgba(0, 0, 0, 0.06);
  border-radius: 15px;
  flex: 2;
}

.stacked-charts-container {
  display: flex;
  flex-direction: row;
}

.hard-costs-container,
.soft-costs-container {
  display: flex;
  flex-direction: row;
}

.chart-title,
.chart-subtitle {
  margin: 0px 15px 15px 0px;
}

.chart-title {
  font-size: 16px;
  font-family: $specialFontBold
}

.chart-subtitle {
  font-size: 14px;
}

.avg-summary-container {
  display: flex;
  flex-direction: row;
  text-align: right;
}

.avg-subtitle {
  margin: 0 5px 0 20px;
}

.summary-value {
  font-weight: bold;
}

.avg-value-original {
  color: #4A5FD5;
}

.doughnut-container {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
}

/* 🔥 FIX: Wrapper for doughnut chart with center text overlay */
.doughnut-chart-wrapper {
  position: relative;
  display: inline-block;
}

.doughnut-center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}

.doughnut-center-value {
  color: #6ED0CD;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
}

.doughnut-center-label {
  color: #52596D;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  margin-top: 2px;
}

.avg-value-revised {
  color: #84CADE;

}

.top-offenders-subtitle {
  visibility: hidden;
}

// Doughnut Legend
.doughnut-chart-legend {
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 5%;

  .acquisition-legend {
    margin: 5px 0;

    .detail-container {
      display: flex;
      flex-direction: row;
      color: $darkGray1
    }

    .vertical-divider {
      border-left: 5px solid $darkGray1;
      margin: 0 10px;
    }
  }

  .hard-legend {
    margin: 5px 0;

    .detail-container {
      display: flex;
      flex-direction: row;
      color: $brightBlue1;
    }

    .vertical-divider {
      border-left: 5px solid $brightBlue1;
      margin: 0 10px;
    }
  }

  .soft-legend {
    margin: 5px 0;

    .detail-container {
      display: flex;
      flex-direction: row;
      color: $mediumBlue1;
    }

    .vertical-divider {
      border-left: 5px solid $mediumBlue1;
      margin: 0 10px;
    }
  }

  .misc-finance-legend {
    margin: 5px 0;

    .detail-container {
      display: flex;
      flex-direction: row;
      color: $mediumGray1;
    }

    .vertical-divider {
      border-left: 5px solid $mediumGray1;
      margin: 0 10px;
    }
  }

  .contingencies-legend {
    margin: 5px 0;

    .detail-container {
      display: flex;
      flex-direction: row;
      color: #d3d3d3;
    }

    .vertical-divider {
      border-left: 5px solid #d3d3d3;
      margin: 0 10px;
    }
  }
}

.summary-stacked-bar {
  padding: 0 10px;
}

.budget-summary-header-container {
  margin-top: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  .budget-summary-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
}

.budget-tracking-header-container {
  margin-top: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  .budget-tracking-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
  
  .subject-property-container {
    display: flex;
    align-items: center;
    margin-left: auto;
    
    form {
      display: flex;
      align-items: center;
    }
    
    label {
      font-size: 14px;
      font-weight: bold;
      color: $navyBlue;
      margin-right: 10px;
    }
    
    input {
      width: 150px;
      padding: 5px;
      border: 1px solid $lightGray1;
      border-radius: 4px;
      font-size: 14px;
      
      &:focus {
        border-color: $brightBlue1;
        outline: none;
      }
    }
    
    .apply-button {
      margin-left: 10px;
      padding: 5px 10px;
      background-color: $brightBlue1;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      
      &:hover {
        background-color: darken($brightBlue1, 10%);
      }
    }
  }
}

.custom-select {
  position: relative;
  font-family: inherit;
  display: flex;
  align-items: center;
  background: none;

  select {
    border: 0;
    color: $brightBlue1;
    font-size: 1.25rem;
    background: none;
  }

  select:focus {
      border-color: $brightBlue1;
      outline: $brightBlue1;
  }
}

.right-div {
    flex: 1;
}
</style>
